'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useTranslations } from 'next-intl';
import { Trash2 } from 'lucide-react';
import type { Table } from '@/lib/table-features';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { useDeleteDynamicFormMutation } from '../services/dynamic-forms.mutations';
import { DYNAMIC_FORMS_HASH } from '../constants';
import type { DynamicForm } from '../types';

interface DynamicFormsTableActionBarProps {
  table: Table<DynamicForm>;
}

export function DynamicFormsTableActionBar({
  table
}: DynamicFormsTableActionBarProps) {
  const t = useTranslations('dynamicForms');
  const tc = useTranslations('common');
  const { open: bulkOpen, onOpenChange: toggleBulk } = useHashDialog(
    DYNAMIC_FORMS_HASH.bulkDelete
  );

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const deleteForm = useDeleteDynamicFormMutation();
  const isDeleting = deleteForm.isPending;

  const handleBulkDelete = async () => {
    const ids = selectedRows.map((row) => row.original.id);
    try {
      for (const id of ids) {
        await deleteForm.mutateAsync(id);
      }
      toggleBulk(false);
      table.resetRowSelection();
    } catch {
      // mutation meta handles error toasts
    }
  };

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: selectedRows.length })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => toggleBulk(true)}
            disabled={isDeleting}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>
        </div>
      </div>

      <AlertDialog open={bulkOpen} onOpenChange={toggleBulk}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirm', { count: selectedRows.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='flex justify-end gap-3'>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className='bg-destructive hover:bg-destructive/90'
            >
              {isDeleting ? '...' : t('delete')}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
